import { UserGroupMemberLookupStatus } from '@navio/nsdk';
import { useStore } from '@tanstack/react-form';
import { noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiInfoBanner, useAppForm, zodPhoneNumberSchema } from 'noddi-ui-web';
import { z } from 'zod';
import { AddMemberStepLayout } from './AddMemberStepLayout';
import type { AddMemberLookup } from './addMemberWizardSteps';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';

const numberSchema = z.object({ phoneNumber: zodPhoneNumberSchema });

const FORM_ID = 'add-member-number-form';

type Props = {
  userGroupId: number;
  /** Kept when stepping back, so a corrected number starts from what was typed. */
  phoneNumber: string;
  translations: NotificationGroupOverviewTranslations;
  /** Called only for a number that can be added; a dead end stays on this step with its banner. */
  onLookedUp: (lookup: AddMemberLookup) => void;
  onCancel: () => void;
};

export const AddMemberNumberStep = ({ userGroupId, phoneNumber, translations: t, onLookedUp, onCancel }: Props) => {
  const form = useAppForm({
    defaultValues: { phoneNumber } satisfies z.input<typeof numberSchema>,
    validators: { onSubmit: numberSchema },
    onSubmit: () => lookUpTypedNumber()
  });

  // The form owns the number and the lookup is keyed on it, so editing moves the query to a key
  // nothing has answered yet and the previous verdict disappears with it.
  const typedNumber = useStore(form.store, (state) => state.values.phoneNumber);
  const {
    data: lookup,
    error,
    refetch
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroupMemberLookup,
    input: { userGroupId, phoneNumber: typedNumber },
    queryConfig: { enabled: false, staleTime: 0 }
  });

  // Declared after the query it runs; only ever called from the submit handler.
  async function lookUpTypedNumber() {
    const { data } = await refetch();
    if (
      data?.status === UserGroupMemberLookupStatus.AVAILABLE ||
      data?.status === UserGroupMemberLookupStatus.NOT_FOUND
    ) {
      onLookedUp({ phoneNumber: typedNumber, status: data.status, displayName: data.displayName ?? '' });
    }
  }

  return (
    <form.AppForm>
      <form
        id={FORM_ID}
        className='flex min-h-0 flex-1 flex-col gap-4'
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          form.handleSubmit();
        }}
      >
        <AddMemberStepLayout
          secondary={{ label: t.cancel, onClick: onCancel }}
          action={<form.SubscribeButton formId={FORM_ID} label={t.next} className='flex-[2]' />}
        >
          <form.AppField name='phoneNumber'>{(field) => <field.PhoneInput label={t.phoneNumber} />}</form.AppField>
          {error && <ApiErrorMessageInternal error={error} />}
          {/* The two dead ends: everything else moves on, so only a blocked number says anything here. */}
          {lookup?.status === UserGroupMemberLookupStatus.ALREADY_MEMBER && (
            <NoddiInfoBanner
              variant='info'
              size='small'
              texts={[{ key: lookup.displayName ?? undefined, value: t.memberAlreadyInGroup }]}
            />
          )}
          {lookup?.status === UserGroupMemberLookupStatus.UNAVAILABLE && (
            <NoddiInfoBanner variant='warning' size='small' texts={[{ value: t.memberUnavailable }]} />
          )}
        </AddMemberStepLayout>
      </form>
    </form.AppForm>
  );
};
